import { apiClient } from 'shared/api/client';
import { endpoints } from 'shared/api/endpoints';
import { fetchWithFallback } from 'shared/data/fetchWithFallback';
import { mapDbBatch } from 'shared/data/mappers';
import { fetchMyBatchAssignments } from 'shared/data/repositories/assignmentsRepository';
import { getSupabase } from 'shared/supabase/client';
import type { Assignment, Batch } from 'types';

export async function fetchBatches(): Promise<Batch[]> {
  return fetchWithFallback(
    async () => {
      const { data, error } = await getSupabase()
        .from('inst_batches')
        .select('*, core_schools!inner(slug), teacher:core_users!teacher_id(first_name, last_name), core_batch_topics(name_en, is_other)')
        .is('deleted_at', null);
      if (error) throw error;
      return (data ?? []).map((row) => mapDbBatch(row));
    },
    async () => {
      const { data } = await apiClient.get<Batch[]>(endpoints.batches.list);
      return data;
    }
  );
}

export async function fetchBatchById(
  id: string
): Promise<Batch & { assignments?: Assignment[] }> {
  return fetchWithFallback(
    async () => {
      const { data, error } = await getSupabase()
        .from('inst_batches')
        .select('*, core_schools!inner(slug), teacher:core_users!teacher_id(first_name, last_name), core_batch_topics(name_en, is_other)')
        .eq('id', id)
        .is('deleted_at', null)
        .single();
      if (error) throw error;

      const batch = mapDbBatch(data);
      let assignments: Assignment[] = [];
      try {
        assignments = await fetchMyBatchAssignments(id);
      } catch {
        assignments = [];
      }

      let studentCount = batch.studentCount;
      let dueAssignmentCount: number | undefined;
      let overdueAssignmentCount: number | undefined;
      let nextDueAt: string | null | undefined;
      try {
        const { data: statsData, error: statsError } = await getSupabase().rpc(
          'stud_my_batch_card_stats',
          { p_batch_ids: [id] }
        );
        if (!statsError) {
          const payload = statsData as {
            ok?: boolean;
            batches?: Array<{
              batch_id: string;
              student_count: number;
              due_assignment_count: number;
              overdue_assignment_count: number;
              next_due_at: string | null;
            }>;
          };
          const row = payload?.ok ? payload.batches?.[0] : undefined;
          if (row) {
            studentCount = row.student_count;
            dueAssignmentCount = row.due_assignment_count;
            overdueAssignmentCount = row.overdue_assignment_count;
            nextDueAt = row.next_due_at;
          }
        }
      } catch {
        // optional enrichment
      }

      return {
        ...batch,
        studentCount,
        dueAssignmentCount,
        overdueAssignmentCount,
        nextDueAt,
        assignments,
      };
    },
    async () => {
      const { data } = await apiClient.get<Batch & { assignments?: Assignment[] }>(
        endpoints.batches.detail(id)
      );
      return data;
    }
  );
}
